/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useEffect } from 'react';
import {
  Button, Grid, Paper, Typography, useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { Sanitize } from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/Sanitizer';
import './styles.scss';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import RadioField
  from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/FormFields/RadioField';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import AppointmentFormSkeleton from './AppointmentFormSkeleton';
import { useAppointmentForm } from './useAppointmentForm';

/**
 * AppointmentForm Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function AppointmentForm(props) {
  const { values } = useFormikContext();
  const { secondStepContent, setNextButtonIsActive, formField: { dueDate } } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    data,
    isLoading,
    activeStep,
    slots,
    slotsToRender,
    handleBack,
    handleNext,
  } = useAppointmentForm();

  // handle button state on step switching and appointment reset
  useEffect(() => {
    if (values.dueDate) {
      setNextButtonIsActive(true);
    } else {
      setNextButtonIsActive(false);
    }
  }, [setNextButtonIsActive, values.dueDate]);

  /**
     * @returns {JSX.Element}
     */
  const getActiveSelectedDateNode = (dateTime) => {
    if (dateTime) {
      const dateParts = dateTime.split(' ');
      return (
        <>
          <span className="date">{dateParts[0]}</span>
          <span className="date-day">{dateParts[1]}</span>
        </>
      );
    }
    return null;
  };

  // If the selected studio does not have any slots return a message for the user
  if (data && data.slots && data.slots.length < 1) {
    setNextButtonIsActive(false);
    return (<Typography variant="subtitle2">{t('noStudios')}</Typography>);
  }

  /**
     * the dom section which will be loaded with a skeleton as loading indicator
     * @return {JSX.Element}
     */
  const renderFormElements = () => {
    // While loading return the loading overlay
    if (isLoading) {
      return <AppointmentFormSkeleton />;
    }

    return (
      <>
        <Grid item xs={12} className="date-container">
          <Button
            className="slot-button prev"
            size="small"
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            {theme.direction === 'rtl' ? (<KeyboardArrowRight />) : (<KeyboardArrowLeft />)}
            {t('prevDay')}
          </Button>
          <Paper
            square
            elevation={0}
            sx={{
              display: 'flex', alignItems: 'center', bgcolor: 'background.default',
            }}
          >
            <Typography
              className="activeDate"
            >
              {getActiveSelectedDateNode(Object.keys(slots)[activeStep])}
            </Typography>
          </Paper>
          <Button
            className="slot-button next"
            size="small"
            onClick={handleNext}
            disabled={activeStep === Object.keys(slots).length - 1}
          >
            {theme.direction === 'rtl' ? (<KeyboardArrowLeft />) : (<KeyboardArrowRight />)}
            {t('nextDay')}
          </Button>
        </Grid>
        <Grid item xs={12} className="appointment-container">
          <Grid container spacing={2}>
            <Grid item xs={12} className="appointments">
              <RadioField
                name={dueDate.name}
                label={dueDate.label}
                data={slotsToRender}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>{t('currentChoice')}</Typography>
          <Typography variant="subtitle2">
            {t('yourChoice')}
            {' '}
            {values.studioName}
          </Typography>
          <Typography variant="subtitle2">
            {t('trainingForm')}
            {' '}
            {values.formOfTraining === 'true' ? t('withTrainer') : t('withoutTrainer')}
          </Typography>
        </Grid>
      </Grid>
      <Sanitize value={secondStepContent} />
      <Grid container spacing={2}>
        {renderFormElements()}
      </Grid>
    </>
  );
}

AppointmentForm.propTypes = {
  secondStepContent: PropTypes.object.isRequired,
  setNextButtonIsActive: PropTypes.func.isRequired,
  formField: PropTypes.object.isRequired,
};
